/** Client-side photo compression before upload (the server accepts jpg/png/webp up to 5 MB). */

export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024

export interface Size {
  width: number
  height: number
}

export interface ImageCodec {
  decode(file: Blob): Promise<Size & { source?: unknown }>
  encode(decoded: { source?: unknown }, size: Size, type: string, quality: number): Promise<Blob>
}

export interface CompressOptions {
  maxEdge?: number
  quality?: number
}

export function fitWithin(width: number, height: number, maxEdge: number): Size {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }
  const ratio = maxEdge / longest
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) }
}

/** Real browser codec: createImageBitmap + canvas. Not available in jsdom, so tests inject a fake. */
export const browserCodec: ImageCodec = {
  async decode(file) {
    const bitmap = await createImageBitmap(file)
    return { width: bitmap.width, height: bitmap.height, source: bitmap }
  },
  async encode(decoded, size, type, quality) {
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('El lienzo (canvas) no está disponible')
    context.drawImage(decoded.source as ImageBitmap, 0, 0, size.width, size.height)
    return new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No se pudo codificar la imagen'))), type, quality),
    )
  },
}

export async function compressImage(file: File, options: CompressOptions = {}, codec: ImageCodec = browserCodec): Promise<File> {
  if (!ACCEPTED_TYPES.includes(file.type)) throw new Error('Solo se pueden adjuntar fotos jpg, png o webp.')
  const maxEdge = options.maxEdge ?? 1600
  const quality = options.quality ?? 0.8
  try {
    const decoded = await codec.decode(file)
    const size = fitWithin(decoded.width, decoded.height, maxEdge)
    const blob = await codec.encode(decoded, size, 'image/jpeg', quality)
    if (blob.size >= file.size) {
      if (file.size > MAX_UPLOAD_BYTES) throw new Error('Esta foto es demasiado grande (más de 5 MB) incluso después de comprimirla.')
      return file
    }
    const name = file.name.replace(/\.(png|webp|jpe?g)$/i, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg' })
  } catch (error) {
    if (error instanceof Error && /demasiado grande/.test(error.message)) throw error
    if (file.size > MAX_UPLOAD_BYTES) throw new Error('Esta foto es demasiado grande (más de 5 MB) y no se pudo comprimir.', { cause: error })
    return file
  }
}
